/**
 * Section Historique de Trésorerie
 * Affiche les mouvements financiers passés avec navigation année/mois
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Building2,
  FileText,
  Home,
  History,
  CalendarDays,
} from 'lucide-react';
import { useTresorerieHistorique } from '@/hooks/useTresorerieHistorique';
import type { FluxTresorerie, CategorieFlux, ModeAffichageTresorerie } from '@/types/tresorerie';
import { CATEGORIES_LABELS, CATEGORIES_COLORS } from '@/types/tresorerie';

interface TresorerieHistoriqueProps {
  entrepriseId: string | null;
  mode?: ModeAffichageTresorerie;
}

// =============================================================================
// Utilitaires
// =============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function getCategorieLabel(categorie: string): string {
  return CATEGORIES_LABELS[categorie as CategorieFlux] || categorie;
}

// =============================================================================
// Composants internes
// =============================================================================

function FluxItemHistorique({
  flux,
  isEncaissement,
  mode = 'ttc',
}: {
  flux: FluxTresorerie;
  isEncaissement: boolean;
  mode?: ModeAffichageTresorerie;
}) {
  const colorClass = isEncaissement ? 'text-green-500' : 'text-red-500';
  const bgColor = CATEGORIES_COLORS[flux.categorie as CategorieFlux] || '#6b7280';
  const montant = mode === 'ht' ? flux.montant_ht : flux.montant;

  return (
    <div className="flex items-start justify-between border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{flux.libelle}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: bgColor, color: bgColor }}
          >
            {getCategorieLabel(flux.categorie)}
          </Badge>
          {flux.chantier_nom && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Building2 className="h-3 w-3" />
              {flux.chantier_nom}
            </span>
          )}
        </div>
      </div>
      <div className="text-right ml-4 flex-shrink-0">
        <p className={cn('font-medium', colorClass)}>
          {isEncaissement ? '+' : '-'}
          {formatCurrency(montant)}
        </p>
        <p className="text-xs text-muted-foreground">
          {format(new Date(flux.date_prevue), 'dd MMM', { locale: fr })}
        </p>
      </div>
    </div>
  );
}

function ChargeFixeItemHistorique({ flux, mode = 'ttc' }: { flux: FluxTresorerie; mode?: ModeAffichageTresorerie }) {
  const bgColor = CATEGORIES_COLORS[flux.categorie as CategorieFlux] || '#94a3b8';
  const jourPrelevement = flux.date_prevue ? parseInt(flux.date_prevue.split('-')[2], 10) : null;
  const montant = mode === 'ht' ? flux.montant_ht : flux.montant;

  return (
    <div className="flex items-start justify-between border-b border-border/50 pb-3 last:border-0 last:pb-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{flux.libelle}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge
            variant="outline"
            className="text-xs"
            style={{ borderColor: bgColor, color: bgColor }}
          >
            Charge fixe
          </Badge>
        </div>
      </div>
      <div className="text-right ml-4 flex-shrink-0">
        <p className="font-medium text-red-500">
          -{formatCurrency(montant)}
        </p>
        {jourPrelevement && (
          <p className="text-xs text-muted-foreground">
            le {jourPrelevement}
          </p>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-6 w-48 mb-4" />
            <div className="space-y-3">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="text-center py-8">
          <CalendarDays className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Aucun mouvement ce mois</p>
          <p className="text-sm text-muted-foreground mt-1">
            Sélectionnez un autre mois pour voir l'historique
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Composant principal
// =============================================================================

export function TresorerieHistorique({ entrepriseId, mode = 'ttc' }: TresorerieHistoriqueProps) {
  const {
    moisData,
    isLoading,
    anneesDisponibles,
    moisDisponibles,
    anneeSelectionnee,
    moisSelectionne,
    setAnnee,
    setMois,
  } = useTresorerieHistorique(entrepriseId);

  const isHt = mode === 'ht';
  const getMontant = (flux: FluxTresorerie) => isHt ? flux.montant_ht : flux.montant;

  // Séparer les décaissements en 2 groupes
  const decaissementsChantiers = moisData?.decaissements.filter(d => d.categorie !== 'charges_fixes') || [];
  const chargesFixes = moisData?.decaissements.filter(d => d.categorie === 'charges_fixes') || [];
  const totalChantiers = decaissementsChantiers.reduce((sum, d) => sum + getMontant(d), 0);
  const totalChargesFixes = chargesFixes.reduce((sum, d) => sum + getMontant(d), 0);

  // Vérifier si aucun mouvement
  const aucunMouvement = moisData &&
    moisData.encaissements.length === 0 &&
    moisData.decaissements.length === 0;

  return (
    <div className="mt-8 space-y-4">
      {/* En-tête avec selects */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Historique de trésorerie</h2>
        </div>

        <div className="flex items-center gap-3">
          {/* Select Année */}
          <Select
            value={String(anneeSelectionnee)}
            onValueChange={(val) => setAnnee(Number(val))}
          >
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Année" />
            </SelectTrigger>
            <SelectContent>
              {anneesDisponibles.map((annee) => (
                <SelectItem key={annee} value={String(annee)}>
                  {annee}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Select Mois */}
          <Select
            value={moisSelectionne}
            onValueChange={setMois}
            disabled={moisDisponibles.length === 0}
          >
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Mois" />
            </SelectTrigger>
            <SelectContent>
              {moisDisponibles.map((mois) => (
                <SelectItem key={mois.value} value={mois.value}>
                  {mois.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : aucunMouvement ? (
        <EmptyState />
      ) : moisData ? (
        <div className="space-y-4">
          {/* Grille encaissements / décaissements */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Encaissements */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowDownCircle className="h-5 w-5 text-green-500" />
                  <h3 className="font-semibold">Encaissements</h3>
                  <span className="ml-auto text-green-500 font-bold">
                    +{formatCurrency(isHt ? moisData.total_encaissements_ht : moisData.total_encaissements)}
                  </span>
                </div>

                {moisData.encaissements.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">
                    Aucun encaissement ce mois
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {moisData.encaissements.map((flux) => (
                      <FluxItemHistorique key={flux.id} flux={flux} isEncaissement={true} mode={mode} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Décaissements */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowUpCircle className="h-5 w-5 text-red-500" />
                  <h3 className="font-semibold">Décaissements</h3>
                  <span className="ml-auto text-red-500 font-bold">
                    -{formatCurrency(isHt ? moisData.total_decaissements_ht : moisData.total_decaissements)}
                  </span>
                </div>

                {moisData.decaissements.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">
                    Aucun décaissement ce mois
                  </p>
                ) : (
                  <div className="space-y-4 max-h-[300px] overflow-y-auto">
                    {/* Sous-section : Chantiers */}
                    {decaissementsChantiers.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium text-muted-foreground">Chantiers</span>
                          <span className="ml-auto text-sm text-red-500 font-medium">
                            -{formatCurrency(totalChantiers)}
                          </span>
                        </div>
                        <div className="space-y-3 pl-2">
                          {decaissementsChantiers.map((flux) => (
                            <FluxItemHistorique key={flux.id} flux={flux} isEncaissement={false} mode={mode} />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sous-section : Charges fixes */}
                    {chargesFixes.length > 0 && (
                      <div className={decaissementsChantiers.length > 0 ? 'mt-4 pt-4 border-t border-border/50' : ''}>
                        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                          <Home className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium text-muted-foreground">Charges fixes</span>
                          <span className="ml-auto text-sm text-red-500 font-medium">
                            -{formatCurrency(totalChargesFixes)}
                          </span>
                        </div>
                        <div className="space-y-3 pl-2">
                          {chargesFixes.map((flux) => (
                            <ChargeFixeItemHistorique key={flux.id} flux={flux} mode={mode} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Récap variation */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Variation du mois</p>
                  <p className="text-xs text-muted-foreground">
                    {moisData.label}
                  </p>
                </div>
                <p
                  className={cn(
                    'text-2xl font-bold',
                    (isHt ? moisData.variation_ht : moisData.variation) >= 0 ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  {(isHt ? moisData.variation_ht : moisData.variation) >= 0 ? '+' : ''}
                  {formatCurrency(isHt ? moisData.variation_ht : moisData.variation)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
